/**
 * @fileoverview Pure vouching helpers for bank transaction entries (MOR-574)
 * @module jobs/summarization/agents/bankTransactions/vouchCore
 *
 * Vouching = matching the cleaned bank-transaction list against the source
 * accounting records (Xero / GL / ledger) to confirm the substantive reality
 * (money that actually left the bank) and to carry the ledger characterisation
 * across so discrepancies surface for the voidable-transactions analysis.
 *
 * IMPORTANT: the ledger does NOT live in `bankTransactionEntries`. GL / Xero
 * exports are separate library documents that the practitioner picks; their
 * lines are parsed deterministically from the spreadsheet (see the frontend
 * `parseLedger.ts`) and passed in here as `LedgerRow[]`. So `computeVouch`
 * takes two independent inputs — bank entries and ledger rows — and never
 * mixes ledger data into the bank agent's table.
 *
 * Match key = normalizedDate | abs(amount). Deliberately NOT the dedup
 * fingerprint:
 *   - A Xero **General Ledger Detail** has no account number (grouped by GL
 *     account name), so account can't be in the key.
 *   - The Debit/Credit columns flip sign between the bank-account section and
 *     the expense sections, so `direction` is unreliable across bank-vs-ledger.
 *   - Bank and ledger descriptions for the same payment rarely match.
 *
 * Match semantics = **presence-based**, not 1:1 count pairing. A bank row is
 * vouched if its date+amount appears anywhere in the ledger; a ledger key with
 * no bank counterpart is ledger-only ("candidate disregard"). Presence is
 * robust to GL **double-entry double-counting** (a full GL lists each payment
 * twice — expense side + bank-account side, same date+amount).
 *
 * Contains no Convex server imports so it can run both server-side and in the
 * browser, exactly like `dedupCore`.
 */

import type { Id } from "@cvx/_generated/dataModel";
import { normalizeDate } from "./dedupCore";

export type VouchStatus = "vouched" | "bank_only";

/** A ledger line parsed from a GL / Xero export (see frontend parseLedger.ts). */
export interface LedgerRow {
  date: string;
  amount: number;
  direction?: "debit" | "credit";
  description?: string;
  account?: string;
  reference?: string;
  /** The source library document this row was parsed from. */
  sourceDocumentId?: string;
}

/** Ledger characterisation carried onto a vouched bank row. */
export interface LedgerCharacterisation {
  account?: string;
  description?: string;
  direction?: "debit" | "credit";
}

export interface BankVouchInput {
  _id: Id<"bankTransactionEntries">;
  date: string;
  amount: number;
  /** Computed by `computeDedup` — duplicates are excluded from vouching. */
  isDuplicate?: boolean;
  vouchOverride?: {
    action: "force_vouched" | "force_unmatched";
    reason?: string;
  };
}

export interface VouchResult {
  vouchKey: string;
  vouchStatus: VouchStatus;
  ledgerCharacterisation?: LedgerCharacterisation;
}

export interface VouchComputation {
  /** Per bank-entry vouch result (only non-duplicate rows are included). */
  byEntryId: Map<Id<"bankTransactionEntries">, VouchResult>;
  /** Ledger rows whose date+amount has no bank counterpart — candidate disregard. */
  ledgerOnly: LedgerRow[];
  summary: { vouched: number; bankOnly: number; ledgerOnly: number };
}

/** date | abs(amount) — no account, direction, or description (see file docs). */
export function buildVouchKey(entry: { date: string; amount: number }): string {
  return `${normalizeDate(entry.date)}|${Math.abs(entry.amount).toFixed(2)}`;
}

/**
 * Computes vouch state by matching bank entries against separately-parsed
 * ledger rows. Pure function, safe to call from queries and from the browser.
 * Practitioner `vouchOverride`s win over the computed result.
 *
 * `vouchingActive` is the caller's concern (is a ledger document configured?) —
 * this function simply matches whatever it's given. With an empty ledger every
 * bank row is `bank_only`, so callers gate display on having ledger rows.
 */
export function computeVouch(
  bankEntries: BankVouchInput[],
  ledgerRows: LedgerRow[],
): VouchComputation {
  // Index ledger rows by match key.
  const ledgerByKey = new Map<string, LedgerRow[]>();
  for (const row of ledgerRows) {
    const key = buildVouchKey(row);
    const bucket = ledgerByKey.get(key) ?? [];
    bucket.push(row);
    ledgerByKey.set(key, bucket);
  }

  // Keys present on the (non-duplicate) bank side — used to find ledger-only.
  const bankKeys = new Set<string>();
  for (const entry of bankEntries) {
    if (entry.isDuplicate) continue;
    bankKeys.add(buildVouchKey(entry));
  }

  // Resolve the vouch status (and, when vouched, the matched ledger row's
  // characterisation) for a single bank entry.
  const resolveVouch = (
    entry: BankVouchInput,
    key: string,
  ): {
    vouchStatus: VouchStatus;
    characterisation?: LedgerCharacterisation;
  } => {
    if (entry.vouchOverride) {
      const vouchStatus: VouchStatus =
        entry.vouchOverride.action === "force_vouched"
          ? "vouched"
          : "bank_only";
      if (vouchStatus !== "vouched") return { vouchStatus };
      const match = ledgerByKey.get(key)?.[0];
      return {
        vouchStatus,
        characterisation: match
          ? {
              account: match.account,
              description: match.description,
              direction: match.direction,
            }
          : undefined,
      };
    }

    const matches = ledgerByKey.get(key);
    if (matches && matches.length > 0) {
      return {
        vouchStatus: "vouched",
        characterisation: {
          account: matches[0].account,
          description: matches[0].description,
          direction: matches[0].direction,
        },
      };
    }
    return { vouchStatus: "bank_only" };
  };

  const byEntryId = new Map<Id<"bankTransactionEntries">, VouchResult>();

  for (const entry of bankEntries) {
    if (entry.isDuplicate) continue;
    const key = buildVouchKey(entry);
    const { vouchStatus, characterisation } = resolveVouch(entry, key);

    byEntryId.set(entry._id, {
      vouchKey: key,
      vouchStatus,
      ledgerCharacterisation:
        vouchStatus === "vouched" ? characterisation : undefined,
    });
  }

  const vouched = [...byEntryId.values()].filter(
    (v) => v.vouchStatus === "vouched",
  ).length;
  const bankOnly = byEntryId.size - vouched;

  // Ledger rows whose key has no bank counterpart — booked but never banked.
  const ledgerOnly: LedgerRow[] = [];
  for (const [key, rows] of ledgerByKey) {
    if (!bankKeys.has(key)) ledgerOnly.push(...rows);
  }

  return {
    byEntryId,
    ledgerOnly,
    summary: { vouched, bankOnly, ledgerOnly: ledgerOnly.length },
  };
}
